import AnnouncementsAPI from "../../api/announcements";
import AnnouncementsManageAPI from "../../api/announcementsManage";

const types = {
  SET_UI_FLAGS: "SET_UI_FLAGS",
  SET_ANNOUNCEMENTS: "SET_ANNOUNCEMENTS",
  SET_PENDING: "SET_PENDING",
  REMOVE_PENDING: "REMOVE_PENDING",
  SET_META: "SET_META",
  SET_UNREAD_COUNT: "SET_UNREAD_COUNT",
  SET_TARGETS: "SET_TARGETS",
  SET_MANAGED: "SET_MANAGED",
  SET_MANAGED_META: "SET_MANAGED_META",
  REMOVE_MANAGED: "REMOVE_MANAGED",
};

const state = {
  records: {},
  sortOrder: [],
  pendingIds: [],
  unreadCount: 0,
  meta: {
    totalCount: 0,
    currentPage: 1,
  },
  targets: {
    accounts: [],
    canPublishGlobally: false,
  },
  // Lista de gestión (drafts/programadas/publicadas). Slice separado del historial
  // de lectura: los records de acá traen markdown crudo + status, no content_html.
  managed: [],
  managedMeta: {
    totalCount: 0,
    currentPage: 1,
  },
  uiFlags: {
    isFetching: false,
    isFetchingPending: false,
    isMarkingSeen: false,
    isFetchingTargets: false,
    isCreating: false,
    isFetchingManaged: false,
    isSaving: false,
    isDeleting: false,
  },
};

const getters = {
  getAll($state) {
    return $state.sortOrder.map((id) => $state.records[id]).filter(Boolean);
  },
  getPending($state) {
    return $state.pendingIds.map((id) => $state.records[id]).filter(Boolean);
  },
  getUnreadCount($state) {
    return $state.unreadCount;
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getMeta($state) {
    return $state.meta;
  },
  getTargets($state) {
    return $state.targets;
  },
  getManaged($state) {
    return $state.managed;
  },
  getManagedMeta($state) {
    return $state.managedMeta;
  },
};

const actions = {
  fetchAll: async ({ commit }, { page = 1, perPage = 25 } = {}) => {
    commit(types.SET_UI_FLAGS, { isFetching: true });
    try {
      const { data } = await AnnouncementsAPI.get({ page, perPage });
      const records = data.payload || [];
      commit(types.SET_ANNOUNCEMENTS, records);
      commit(types.SET_META, {
        totalCount: data.meta?.count ?? records.length,
        currentPage: page,
      });
    } finally {
      commit(types.SET_UI_FLAGS, { isFetching: false });
    }
  },

  fetchPending: async ({ commit }) => {
    commit(types.SET_UI_FLAGS, { isFetchingPending: true });
    try {
      const { data } = await AnnouncementsAPI.pending();
      commit(types.SET_PENDING, data.pending || []);
      commit(types.SET_UNREAD_COUNT, data.unread_count ?? 0);
    } finally {
      commit(types.SET_UI_FLAGS, { isFetchingPending: false });
    }
  },

  // Al abrir el panel: bulk-marca seen_at en el server y limpia el badge local.
  markAllSeen: async ({ commit }) => {
    commit(types.SET_UI_FLAGS, { isMarkingSeen: true });
    try {
      await AnnouncementsAPI.markAllSeen();
      commit(types.SET_UNREAD_COUNT, 0);
    } finally {
      commit(types.SET_UI_FLAGS, { isMarkingSeen: false });
    }
  },

  // Cerrar un warning/critical sin aceptar nada (banner "x", o modal sin requires_ack).
  // Siempre incluye "seen": si nunca se vio, el badge lo contaría como no leído para siempre.
  dismiss: async ({ commit, dispatch }, id) => {
    await AnnouncementsAPI.track(id, ["seen", "dismissed"]);
    commit(types.REMOVE_PENDING, id);
    dispatch("fetchPending");
  },

  // "Entendido" en un modal con requires_ack: aceptar implica también dejar de mostrarlo.
  acknowledge: async ({ commit, dispatch }, id) => {
    await AnnouncementsAPI.track(id, ["seen", "acknowledged", "dismissed"]);
    commit(types.REMOVE_PENDING, id);
    dispatch("fetchPending");
  },

  // Cuentas donde el usuario actual puede publicar (para el Compose): todas si
  // es SuperAdmin, o sólo la propia si es admin de cuenta.
  fetchTargets: async ({ commit }) => {
    commit(types.SET_UI_FLAGS, { isFetchingTargets: true });
    try {
      const { data } = await AnnouncementsManageAPI.targets();
      commit(types.SET_TARGETS, {
        accounts: data.accounts || [],
        canPublishGlobally: !!data.can_publish_globally,
      });
    } finally {
      commit(types.SET_UI_FLAGS, { isFetchingTargets: false });
    }
  },

  create: async ({ commit }, payload) => {
    commit(types.SET_UI_FLAGS, { isCreating: true });
    try {
      const { data } = await AnnouncementsManageAPI.create(payload);
      return data;
    } finally {
      commit(types.SET_UI_FLAGS, { isCreating: false });
    }
  },

  // --- Gestión (editar / drafts / programadas / borrado lógico) ---

  fetchManaged: async ({ commit }, { page = 1, perPage = 25 } = {}) => {
    commit(types.SET_UI_FLAGS, { isFetchingManaged: true });
    try {
      const { data } = await AnnouncementsManageAPI.getManaged({
        page,
        perPage,
      });
      commit(types.SET_MANAGED, data.payload || []);
      commit(types.SET_MANAGED_META, {
        totalCount: data.meta?.count ?? 0,
        currentPage: page,
      });
    } finally {
      commit(types.SET_UI_FLAGS, { isFetchingManaged: false });
    }
  },

  // Carga un anuncio para precargar el form de edición (markdown crudo + account_ids).
  fetchOne: async (_ctx, id) => {
    const { data } = await AnnouncementsManageAPI.show(id);
    return data;
  },

  update: async ({ commit }, { id, payload, notifyAgain = false }) => {
    commit(types.SET_UI_FLAGS, { isSaving: true });
    try {
      const { data } = await AnnouncementsManageAPI.update(id, payload, {
        notifyAgain,
      });
      return data;
    } finally {
      commit(types.SET_UI_FLAGS, { isSaving: false });
    }
  },

  destroy: async ({ commit }, id) => {
    commit(types.SET_UI_FLAGS, { isDeleting: true });
    try {
      await AnnouncementsManageAPI.destroy(id);
      commit(types.REMOVE_MANAGED, id);
    } finally {
      commit(types.SET_UI_FLAGS, { isDeleting: false });
    }
  },

  publish: async ({ commit }, id) => {
    commit(types.SET_UI_FLAGS, { isSaving: true });
    try {
      const { data } = await AnnouncementsManageAPI.publish(id);
      return data;
    } finally {
      commit(types.SET_UI_FLAGS, { isSaving: false });
    }
  },
};

const mutations = {
  [types.SET_UI_FLAGS]($state, flags) {
    $state.uiFlags = { ...$state.uiFlags, ...flags };
  },

  // Merge (no overwrite): historial y pending comparten el mismo diccionario de
  // records pero tienen listas de ids separadas, no deben pisarse entre sí.
  [types.SET_ANNOUNCEMENTS]($state, records) {
    const newRecords = { ...$state.records };
    const newOrder = [];
    records.forEach((record) => {
      newRecords[record.id] = record;
      newOrder.push(record.id);
    });
    $state.records = newRecords;
    $state.sortOrder = newOrder;
  },

  [types.SET_PENDING]($state, records) {
    const newRecords = { ...$state.records };
    const ids = [];
    records.forEach((record) => {
      newRecords[record.id] = record;
      ids.push(record.id);
    });
    $state.records = newRecords;
    $state.pendingIds = ids;
  },

  [types.REMOVE_PENDING]($state, id) {
    $state.pendingIds = $state.pendingIds.filter(
      (pendingId) => pendingId !== id,
    );
  },

  [types.SET_META]($state, meta) {
    $state.meta = { ...$state.meta, ...meta };
  },

  [types.SET_UNREAD_COUNT]($state, count) {
    $state.unreadCount = count;
  },

  [types.SET_TARGETS]($state, targets) {
    $state.targets = targets;
  },

  [types.SET_MANAGED]($state, records) {
    $state.managed = records;
  },

  [types.SET_MANAGED_META]($state, meta) {
    $state.managedMeta = { ...$state.managedMeta, ...meta };
  },

  [types.REMOVE_MANAGED]($state, id) {
    $state.managed = $state.managed.filter((record) => record.id !== id);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
